import React from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import AssociateAvatar from '../../components/AssociateAvatar';
import { getAssociateDisplayLabel } from '../../utils';
import { AssignmentAssociate, Workstation, isEmptySlot } from '../models';
import AddAssociateSlot from './AddAssociateSlot';
import './WorkstationCard.scss';

export interface WorkstationCardProps {
  /** The line/workstation to render. */
  workstation: Workstation;
  /**
   * When true, each occupied slot shows the associate's per-station id
   * (`assignedWorkstationId`, e.g. "ws-02-01-R") instead of their name. Empty
   * slots also carry a station id, so they show it here too (in place of the
   * "Add associate" label).
   */
  showWorkstationIds?: boolean;
  /**
   * The unassigned associate pool, used to populate the inline "Add
   * associate" typeahead on empty slots. When omitted (or empty) together with
   * `onAssignToSlot`, empty slots stay read-only placeholders.
   */
  unassignedAssociates?: AssignmentAssociate[];
  /**
   * Fires when the user picks an associate for an empty slot. Receives the
   * chosen associate's id, this workstation's id, and the slot's index within
   * `assignedAssociates`. When omitted, empty slots render the plain read-only
   * "Add associate" placeholder.
   */
  onAssignToSlot?: (associateId: string, workstationId: string, slotIndex: number) => void;
  /**
   * Fires when the user unassigns an occupied slot. Receives the associate's
   * id. When provided (and WS IDs aren't being shown), each occupied slot
   * renders a trailing unassign (✕) icon button; when omitted, occupied slots
   * stay read-only.
   */
  onUnassign?: (associateId: string) => void;
  /**
   * Fires when the user moves an occupied associate to a new location.
   * Receives the associate's id. When provided (and WS IDs aren't being
   * shown), each occupied slot renders a move (↗) icon button just left of the
   * unassign (✕) button; when omitted, no move button is shown.
   */
  onMove?: (associateId: string) => void;
  /**
   * Fires when the user views an occupied associate's assignment decision.
   * Receives the associate's id. When provided (and WS IDs aren't being
   * shown), each occupied slot renders an info (ℹ️) icon button as the
   * leftmost action; when omitted, no info button is shown.
   */
  onViewDecision?: (associateId: string) => void;
  /**
   * Read-only mode (e.g. the published / history view). When true, every
   * mutating affordance is suppressed regardless of the callbacks supplied:
   * the inline "Add associate" typeahead, the unassign (✕), and the move (↗)
   * actions never render, and an empty (planned-but-unfilled) slot shows the
   * neutral "Unassigned" label instead of the actionable "Add associate"
   * placeholder. The view-decision (ℹ️) action is *not* gated — it is itself
   * read-only. Defaults to false so the editable page is unaffected.
   */
  readOnly?: boolean;
}

/**
 * A single line ("workstation") within a process path on the redesigned
 * Station Assignment screen.
 *
 * Each entry in `assignedAssociates` is one planned position: a real associate
 * renders as an occupied slot, while an empty (planned-but-unfilled) slot —
 * identified by {@link isEmptySlot} — renders either the inline
 * {@link AddAssociateSlot} typeahead (when the parent supplies
 * `onAssignToSlot` + an associate pool and WS IDs aren't being shown) or a
 * read-only dashed "Add associate" placeholder. Empty slots are data-driven
 * (the API returns a placeholder entry per unfilled planned position), so the
 * card renders exactly the entries it is given rather than padding to
 * `capacity`.
 *
 * Built from Meridian primitives (Box/Row/Column/Text/Select) plus the shared
 * AssociateAvatar.
 */
const WorkstationCard: React.FC<WorkstationCardProps> = ({
  workstation,
  showWorkstationIds = false,
  unassignedAssociates,
  onAssignToSlot,
  onUnassign,
  onMove,
  onViewDecision,
  readOnly = false,
}) => {
  const slots = workstation.assignedAssociates;
  const filled = slots.filter((a) => !isEmptySlot(a)).length;

  // The info (ℹ️), move (↗) and unassign (✕) affordances show on occupied slots
  // only when a handler is supplied and the card is showing names (not WS IDs).
  // Move and unassign additionally require an editable card; the info action is
  // itself read-only, so it stays available in read-only mode.
  const canViewDecision = !showWorkstationIds && !!onViewDecision;
  const canUnassign = !readOnly && !showWorkstationIds && !!onUnassign;
  const canMove = !readOnly && !showWorkstationIds && !!onMove;
  // The info/move/unassign buttons live in a single trailing actions cell, so
  // the slot Row has a third `fit` column only when at least one action shows.
  const hasActions = canViewDecision || canMove || canUnassign;

  // The inline typeahead only makes sense when we can both commit a pick and
  // offer associates to pick, while the card is showing names (not WS IDs), and
  // while the card is editable.
  const canAddInline =
    !readOnly && !showWorkstationIds && !!onAssignToSlot && (unassignedAssociates?.length ?? 0) > 0;

  return (
    <Column
      className="assignments-workstation-card"
      type="outline"
      spacing="none"
      data-testid={`workstation-${workstation.workstationId}`}
    >
      <Row
        className="assignments-workstation-card__header"
        backgroundColor="subdued"
        alignmentVertical="center"
        spacing="200"
        widths={['fill', 'fit']}
      >
        <Text type="b200" className="assignments-workstation-card__title">
          {workstation.workstationName}
        </Text>
        <Text type="b100" color="secondary" data-testid="workstation-count">
          {filled}/{slots.length}
        </Text>
      </Row>

      <Column className="assignments-workstation-card__body" spacing="100">
        {slots.map((associate, index) =>
          isEmptySlot(associate) ? (
            canAddInline ? (
              <Box
                key={associate.assignedWorkstationId ?? `empty-${index}`}
                className="assignments-workstation-card__slot assignments-workstation-card__slot--add"
                data-testid="slot-empty"
              >
                <AddAssociateSlot
                  unassignedAssociates={unassignedAssociates as AssignmentAssociate[]}
                  onSelect={(associateId) =>
                    (onAssignToSlot as NonNullable<WorkstationCardProps['onAssignToSlot']>)(
                      associateId,
                      workstation.workstationId,
                      index,
                    )
                  }
                  dataTestId={`slot-add-${workstation.workstationId}-${index}`}
                />
              </Box>
            ) : (
              <Box
                key={associate.assignedWorkstationId ?? `empty-${index}`}
                className="assignments-workstation-card__slot assignments-workstation-card__slot--empty"
                backgroundColor="subdued"
                data-testid="slot-empty"
              >
                <Text
                  type="b100"
                  color="secondary"
                  className="assignments-workstation-card__empty-label"
                >
                  {showWorkstationIds && associate.assignedWorkstationId
                    ? associate.assignedWorkstationId
                    : readOnly
                    ? 'Unassigned'
                    : 'Add associate'}
                </Text>
              </Box>
            )
          ) : (
            <Row
              key={associate.associateId}
              className="assignments-workstation-card__slot assignments-workstation-card__slot--occupied"
              backgroundColor="subdued"
              alignmentVertical="center"
              spacing="200"
              widths={hasActions ? ['fit', 'fill', 'fit'] : ['fit', 'fill']}
              data-testid={`slot-${associate.associateId}`}
            >
              <AssociateAvatar
                associateName={associate.associateName}
                associateAlias={associate.associateAlias}
                className="assignments-workstation-card__avatar"
              />
              <Text type="b300" className="assignments-workstation-card__slot-label">
                {showWorkstationIds
                  ? associate.assignedWorkstationId ?? '—'
                  : getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
              </Text>
              {hasActions && (
                <Row
                  className="assignments-workstation-card__slot-actions"
                  spacing="100"
                  alignmentVertical="center"
                  spacingInset="none"
                  width="fit"
                >
                  {canViewDecision && (
                    <Button
                      type="icon"
                      size="small"
                      label={`View decision for ${associate.associateName}`}
                      onClick={() => onViewDecision?.(associate.associateId as string)}
                      data-testid={`slot-info-${associate.associateId}`}
                    >
                      <Icon tokens={infoTokens}>Info</Icon>
                    </Button>
                  )}
                  {canMove && (
                    <Button
                      type="icon"
                      size="small"
                      label={`Move ${associate.associateName}`}
                      onClick={() => onMove?.(associate.associateId as string)}
                      data-testid={`slot-move-${associate.associateId}`}
                    >
                      <Icon tokens={exportSmallTokens}>Move</Icon>
                    </Button>
                  )}
                  {canUnassign && (
                    <Button
                      type="icon"
                      size="small"
                      label={`Unassign ${associate.associateName}`}
                      onClick={() => onUnassign?.(associate.associateId as string)}
                      data-testid={`slot-unassign-${associate.associateId}`}
                    >
                      <Icon tokens={closeSmallTokens}>Unassign</Icon>
                    </Button>
                  )}
                </Row>
              )}
            </Row>
          ),
        )}
      </Column>
    </Column>
  );
};

export default WorkstationCard;

import './WorkstationCard.scss';